/**
 * Client Files tab (ClientDetails → tab index 2)
 *
 * INVESTIGATION / prior state:
 * - Data: folders + files from `/api/clients/...` mixed with multi-tenant PATCH; now unified on `/api/multi-tenant/...` where applicable.
 * - Worked: upload (multipart), download, folder nav (sidebar + breadcrumbs), basic search, row open in new tab, rename/delete/share/e-sign/version menu, bulk archive, drag overlay.
 * - Felt rough: row click always opened new tab (no in-app preview), no double-click/fullscreen, no row context menu, weak loading/empty/error copy,
 *   no filter chips / sort, three-dot menu order/icons differed from product spec, rename used generic PATCH vs rename endpoint, no per-file upload progress,
 *   "New folder" was a toast placeholder, no URL ?folder= sync, limited keyboard/hover polish.
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, IconButton, TextField, Breadcrumbs,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Checkbox,
  Chip, InputAdornment, Tooltip, Divider, List, ListItemButton, ListItemIcon,
  ListItemText, Menu, MenuItem, Snackbar, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Skeleton, FormControl,
  InputLabel, Select, MenuList,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ShareIcon from '@mui/icons-material/Share';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewDrawer from './PreviewDrawer';
import MoveFolderDialog from './MoveFolderDialog';
import ESignaturePrepare from './ESignaturePrepare';
import { API_BASE_URL } from '../constants';
import {
  clientFilesKeys,
  useClientFoldersQuery,
  useClientFileContentsQuery,
  useRenameDocumentMutation,
  useDeleteDocumentMutation,
  useShareDocumentMutation,
  useSignDocumentMutation,
  useMoveDocumentMutation,
  useCreateFolderMutation,
  useUploadDocumentsMutation,
} from '../hooks/useClientFiles';
import api from '../api';
import { useQueryClient } from '@tanstack/react-query';

/** Absolute PDF URL for ESignaturePrepare after create-from-file (API may return relative paths). */
function buildEsignPdfUrl(created, file) {
  const raw = created?.pdf_url;
  if (raw && typeof raw === 'string') {
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = API_BASE_URL.replace(/\/$/, '');
    return `${base}${raw.startsWith('/') ? raw : `/${raw}`}`;
  }
  const u = file?.file_url;
  if (!u || typeof u !== 'string') return '';
  if (/^https?:\/\//i.test(u)) return u;
  const base = API_BASE_URL.replace(/\/$/, '');
  return `${base}${u.startsWith('/') ? u : `/${u}`}`;
}

const kindIcon = (fileName) => {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (ext === 'pdf') return <PictureAsPdfIcon fontSize="small" sx={{ color: 'error.main' }} />;
  if (['doc', 'docx'].includes(ext)) return <DescriptionOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'success.main' }} />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <ImageOutlinedIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
  return <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
};

const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

const ext = (name) => (name || '').split('.').pop().toLowerCase();

const SORT_LABELS = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  name_asc: 'Name A–Z',
  name_desc: 'Name Z–A',
  size: 'Largest first',
};

function applyFilter(files, chip) {
  if (!chip || chip === 'all') return files;
  return files.filter((f) => {
    const e = ext(f.file_name);
    if (chip === 'documents') return ['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(e);
    if (chip === 'spreadsheets') return ['xls', 'xlsx', 'csv'].includes(e);
    if (chip === 'images') return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(e);
    if (chip === 'signed') return f.esignature_status === 'completed' || f.signature_status === 'completed';
    if (chip === 'pending') return f.esignature_status === 'pending' || f.signature_status === 'pending';
    return true;
  });
}

function applySort(files, sortKey) {
  const copy = [...files];
  const byName = (a, b) => (a.file_name || '').localeCompare(b.file_name || '', undefined, { sensitivity: 'base' });
  const byDate = (a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0);
  const bySize = (a, b) => (Number(b.file_size) || 0) - (Number(a.file_size) || 0);
  if (sortKey === 'name_asc') copy.sort((a, b) => byName(a, b));
  else if (sortKey === 'name_desc') copy.sort((a, b) => byName(b, a));
  else if (sortKey === 'oldest') copy.sort((a, b) => byDate(b, a));
  else if (sortKey === 'size') copy.sort((a, b) => bySize(a, b));
  else copy.sort((a, b) => byDate(a, b));
  return copy;
}

const FilesTab = ({ client, highlightFileId, onHighlightConsumed }) => {
  const clientId = client?.id;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderFromUrl = searchParams.get('folder');
  const currentFolderId = folderFromUrl ? Number(folderFromUrl) || null : null;
  const filesParam = searchParams.get('files');
  const filesView = filesParam === 'inbox' || filesParam === 'archived' ? filesParam : null;
  const rootFilesMode = filesView || 'all';

  const [folderPath, setFolderPath] = useState([]);
  const [search, setSearch] = useState('');
  const [filterChip, setFilterChip] = useState('all');
  const [sortKey, setSortKey] = useState('newest');
  const [selected, setSelected] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuFile, setMenuFile] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  /** Sidebar folder id receiving a row drag, or 'root' | 'all' for drop targets */
  const [folderDropTarget, setFolderDropTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [renameDialog, setRenameDialog] = useState({ open: false, file: null, value: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, file: null });
  const [versionDialog, setVersionDialog] = useState({ open: false, file: null, versions: [], loading: false });
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [sharedOverrides, setSharedOverrides] = useState({});
  /** Full-screen “Place signature fields” after e-sign is started from this tab. */
  const [esignPrepare, setEsignPrepare] = useState(null);
  const fileInputRef = useRef();
  const clickTimerRef = useRef(null);

  const showToast = useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const foldersQuery = useClientFoldersQuery(clientId);
  const contentsQuery = useClientFileContentsQuery(clientId, currentFolderId, rootFilesMode);
  const needSeparateInboxCount = Boolean(clientId && (currentFolderId || filesView === 'archived'));
  const inboxCountQuery = useClientFileContentsQuery(clientId, null, 'inbox', {
    enabled: needSeparateInboxCount,
  });

  const renameMut = useRenameDocumentMutation(clientId, showToast);
  const deleteMut = useDeleteDocumentMutation(clientId, showToast);
  const shareMut = useShareDocumentMutation(clientId, showToast);
  const signMut = useSignDocumentMutation(clientId, showToast);
  const moveMut = useMoveDocumentMutation(clientId, showToast);
  const createFolderMut = useCreateFolderMutation(clientId, showToast);
  const uploadMut = useUploadDocumentsMutation(clientId, currentFolderId, showToast);

  const folders = foldersQuery.data || [];
  const subfolders = contentsQuery.data?.subfolders || [];
  const rawFiles = contentsQuery.data?.files || [];
  const loading = foldersQuery.isLoading || contentsQuery.isLoading;
  const loadError = foldersQuery.error || contentsQuery.error;

  const inboxBadgeCount = useMemo(() => {
    if (!currentFolderId && rootFilesMode === 'all') {
      return rawFiles.filter((f) => f.source === 'document_request').length;
    }
    if (!currentFolderId && rootFilesMode === 'inbox') {
      return rawFiles.length;
    }
    return inboxCountQuery.data?.files?.length ?? 0;
  }, [currentFolderId, rootFilesMode, rawFiles, inboxCountQuery.data?.files?.length]);

  const files = useMemo(() => {
    return rawFiles.map((f) => ({
      ...f,
      shared_with_client: sharedOverrides[f.id] ?? f.shared_with_client ?? false,
    }));
  }, [rawFiles, sharedOverrides]);

  useEffect(() => {
    if (highlightFileId && files.length > 0) onHighlightConsumed?.();
  }, [highlightFileId, files, onHighlightConsumed]);

  const setFolderInUrl = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set('folder', String(id));
      next.delete('files');
    } else {
      next.delete('folder');
    }
    setSearchParams(next, { replace: true });
  };

  const goToAllFilesRoot = () => {
    setFolderPath([]);
    const next = new URLSearchParams(searchParams);
    next.delete('folder');
    next.delete('files');
    setSearchParams(next, { replace: true });
    setSelected([]);
  };

  const goToFileInbox = () => {
    setFolderPath([]);
    const next = new URLSearchParams(searchParams);
    next.delete('folder');
    next.set('files', 'inbox');
    setSearchParams(next, { replace: true });
    setSelected([]);
  };

  const goToArchivedRoot = () => {
    setFolderPath([]);
    const next = new URLSearchParams(searchParams);
    next.delete('folder');
    next.set('files', 'archived');
    setSearchParams(next, { replace: true });
    setSelected([]);
  };

  const handleFolderClick = (folder) => {
    setFolderPath((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setFolderInUrl(folder.id);
    setSelected([]);
  };

  const handleBreadcrumb = (index) => {
    if (index === -1) {
      goToAllFilesRoot();
      return;
    }
    const seg = folderPath[index];
    setFolderPath((prev) => prev.slice(0, index + 1));
    setFolderInUrl(seg?.id ?? null);
    setSelected([]);
  };

  const refetchAfterMove = useCallback(() => {
    contentsQuery.refetch();
    foldersQuery.refetch();
    inboxCountQuery.refetch();
  }, [contentsQuery, foldersQuery, inboxCountQuery]);

  useEffect(() => {
    if (!currentFolderId && folderPath.length) setFolderPath([]);
  }, [currentFolderId, folderPath.length]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = files.filter((f) => !s || (f.file_name || '').toLowerCase().includes(s));
    list = applyFilter(list, filterChip);
    return applySort(list, sortKey);
  }, [files, search, filterChip, sortKey]);

  const allSelected = filtered.length > 0 && selected.length === filtered.length;
  const anySelected = selected.length > 0;
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected(allSelected ? [] : filtered.map((f) => String(f.id)));
  const isDocReq = (file) => file && String(file.id).startsWith('dr_');

  const closeMenu = () => {
    setAnchorEl(null);
    setMenuFile(null);
    setContextMenu(null);
  };

  const runMenuAction = (fn) => {
    const f = menuFile;
    closeMenu();
    if (typeof fn === 'function') fn(f);
  };

  const handleDownload = (file) => {
    const url = file.file_url || file.url;
    if (!url) {
      showToast('No file URL available', 'error');
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = file.file_name || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    showToast('Download started', 'success');
  };

  const openPreview = (file, fullscreen) => {
    setPreviewFile(file);
    setPreviewFullscreen(!!fullscreen);
  };

  const handleRowClick = (file) => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      openPreview(file, false);
      clickTimerRef.current = null;
    }, 260);
  };

  const handleRowDoubleClick = (file) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    openPreview(file, true);
  };

  const handleShare = async (file) => {
    const email = client?.primary_contact?.emails?.[0]?.email || client?.primary_contact?.email || client?.email;
    if (!email) {
      showToast('No client email on record', 'error');
      return;
    }
    const prev = sharedOverrides[file.id];
    setSharedOverrides((m) => ({ ...m, [file.id]: true }));
    try {
      await shareMut.mutateAsync({
        file,
        emailRecipients: [email],
        message: `Please find "${file.file_name}" shared with you.`,
      });
    } catch {
      setSharedOverrides((m) => ({ ...m, [file.id]: prev }));
    }
  };

  const handleSign = (file) => {
    if (isDocReq(file)) {
      showToast('E-signature not supported for client-uploaded files', 'error');
      return;
    }
    signMut.mutate(
      { file },
      {
        onSuccess: (data) => {
          if (data?.signing_url || data?.redirect_url || data?.zoho_sign_url) return;
          if (!data?.id) return;
          const pdfUrl = buildEsignPdfUrl(data, file);
          if (pdfUrl) {
            setPreviewFile(null);
            setPreviewFullscreen(false);
            setEsignPrepare({
              documentId: data.id,
              fileName: data.document_name || file.file_name,
              pdfUrl,
            });
            showToast('Click “Add Signature Field”, then tap the PDF to place Signature 1, Signature 2, … Then save or send.', 'success');
          } else {
            showToast('E-signature started — open the E-Signatures tab to place fields (PDF URL missing).', 'warning');
          }
        },
      },
    );
  };

  const handleVersionHistory = async (file) => {
    if (isDocReq(file)) {
      showToast('Version history not available for client uploads', 'error');
      return;
    }
    setVersionDialog({ open: true, file, versions: [], loading: true });
    try {
      const res = await api.get(`/api/files/${file.id}/versions/`);
      setVersionDialog((d) => ({ ...d, versions: res.data?.versions || res.data || [], loading: false }));
    } catch {
      setVersionDialog((d) => ({ ...d, loading: false }));
      showToast('Failed to load version history', 'error');
    }
  };

  const handleRenameSubmit = () => {
    const { file, value } = renameDialog;
    const v = (value || '').trim();
    setRenameDialog({ open: false, file: null, value: '' });
    if (!v || v === file?.file_name) return;
    if (v.length > 255) {
      showToast('Name must be at most 255 characters', 'error');
      return;
    }
    if (/[/\\]/.test(v)) {
      showToast('Name cannot contain slashes', 'error');
      return;
    }
    renameMut.mutate({ fileId: file.id, fileName: v });
  };

  const handleDeleteConfirm = () => {
    const { file } = deleteDialog;
    setDeleteDialog({ open: false, file: null });
    if (file) {
      if (previewFile && String(previewFile.id) === String(file.id)) {
        setPreviewFile(null);
        setPreviewFullscreen(false);
      }
      deleteMut.mutate({ file });
    }
  };

  const handleUpload = (fileList) => {
    if (!fileList?.length) return;
    setUploadPct(0);
    uploadMut.mutate(
      { files: Array.from(fileList), onProgress: (pct) => setUploadPct(pct) },
      { onSettled: () => { setUploadPct(null); if (fileInputRef.current) fileInputRef.current.value = ''; } },
    );
  };

  const handleCreateFolder = () => {
    const n = newFolderName.trim();
    if (!n) return;
    createFolderMut.mutate(
      { name: n, parentFolder: currentFolderId },
      { onSuccess: () => { setNewFolderOpen(false); setNewFolderName(''); } },
    );
  };

  const folderPathLabel = useMemo(() => {
    if (!currentFolderId && filesView === 'inbox') return 'File Inbox';
    if (!currentFolderId && filesView === 'archived') return 'Archived files';
    if (!currentFolderId) return 'All files';
    const names = folderPath.map((p) => p.name).filter(Boolean);
    return ['All files', ...names].join(' / ');
  }, [currentFolderId, folderPath, filesView]);

  const errorDetail = loadError?.response?.data?.detail || loadError?.response?.data?.error || loadError?.message;

  if (!clientId) return null;

  return (
    <Box sx={{ display: 'flex', gap: 2, minHeight: 560, flexDirection: { xs: 'column', md: 'row' } }}>
      <Paper variant="outlined" sx={{ flex: { xs: 'none', md: '0 0 248px' }, p: 1.5, display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, mb: 0.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>Folders</Typography>
          <Tooltip title="New folder">
            <IconButton size="small" color="primary" aria-label="Create new folder" onClick={() => setNewFolderOpen(true)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <List dense disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
          <ListItemButton
            selected={!currentFolderId && !filesView}
            onClick={goToAllFilesRoot}
            sx={{
              borderRadius: 1,
              mb: 0.25,
              bgcolor: folderDropTarget === 'root' ? 'action.selected' : undefined,
            }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setFolderDropTarget('root'); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFolderDropTarget((t) => (t === 'root' ? null : t)); }}
            onDrop={(e) => {
              e.preventDefault();
              setFolderDropTarget(null);
              const raw = e.dataTransfer.getData('application/x-firmflow-file-id');
              if (!raw) return;
              moveMut.mutate(
                { fileId: raw.startsWith('dr_') ? raw : Number(raw), folderId: null },
                { onSettled: refetchAfterMove },
              );
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}><FolderOpenOutlinedIcon fontSize="small" color={!currentFolderId && !filesView ? 'primary' : 'inherit'} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: !currentFolderId && !filesView ? 600 : 400 }} primary="All files" />
          </ListItemButton>
          {folders.map((f) => (
            <ListItemButton
              key={f.id}
              selected={currentFolderId === f.id}
              onClick={() => handleFolderClick(f)}
              sx={{
                borderRadius: 1,
                mb: 0.25,
                pl: 3,
                bgcolor: folderDropTarget === f.id ? 'action.selected' : undefined,
              }}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setFolderDropTarget(f.id); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFolderDropTarget((t) => (t === f.id ? null : t)); }}
              onDrop={(e) => {
                e.preventDefault();
                setFolderDropTarget(null);
                const raw = e.dataTransfer.getData('application/x-firmflow-file-id');
                if (!raw) return;
                moveMut.mutate(
                  { fileId: raw.startsWith('dr_') ? raw : Number(raw), folderId: f.id },
                  { onSettled: refetchAfterMove },
                );
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}><FolderOutlinedIcon fontSize="small" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={f.name} />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ my: 1 }} />
        <List dense disablePadding>
          <ListItemButton
            selected={!currentFolderId && filesView === 'inbox'}
            onClick={goToFileInbox}
            sx={{ borderRadius: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}><InboxOutlinedIcon fontSize="small" color="action" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} primary="File Inbox" />
            <Chip label={String(inboxBadgeCount)} size="small" sx={{ height: 18, fontSize: 11 }} />
          </ListItemButton>
          <ListItemButton selected={!currentFolderId && filesView === 'archived'} onClick={goToArchivedRoot} sx={{ borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 28 }}><ArchiveOutlinedIcon fontSize="small" color="action" /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} primary="Archived files" />
          </ListItemButton>
        </List>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          p: 2,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          position: 'relative',
          outline: dragActive ? `2px dashed` : 'none',
          outlineColor: dragActive ? 'primary.main' : 'transparent',
          outlineOffset: 2,
        }}
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); }}
        onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setDragActive(false); }}
        onDrop={(e) => { e.preventDefault(); setDragActive(false); handleUpload(e.dataTransfer.files); }}
      >
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: dragActive ? 'primary.main' : 'divider',
            bgcolor: dragActive ? 'action.selected' : 'action.hover',
            textAlign: 'center',
          }}
        >
          <UploadFileOutlinedIcon sx={{ fontSize: 36, color: 'primary.main', mb: 0.5 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {currentFolderId
              ? 'Drag files here or use Upload files — they are stored in this folder.'
              : 'Drag files here or use Upload files — uploads appear at the top level and are counted in File Inbox until you move them into a folder.'}
          </Typography>
          {uploadPct != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{uploadPct}%</Typography>
          )}
          <Button variant="contained" startIcon={<UploadFileOutlinedIcon />} sx={{ textTransform: 'none', fontWeight: 600 }} disabled={uploadMut.isPending} onClick={() => fileInputRef.current?.click()}>
            Upload files
          </Button>
          <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => handleUpload(e.target.files)} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Breadcrumbs separator="›" sx={{ fontSize: 14 }}>
            <Typography
              variant="body2"
              color={!currentFolderId ? 'text.primary' : 'text.secondary'}
              fontWeight={!currentFolderId ? 600 : 400}
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={goToAllFilesRoot}
            >
              {currentFolderId
                ? 'All files'
                : filesView === 'inbox'
                  ? 'File Inbox'
                  : filesView === 'archived'
                    ? 'Archived files'
                    : 'All files'}
            </Typography>
            {folderPath.map((seg, i) =>
              i < folderPath.length - 1
                ? <Typography key={seg.id} variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => handleBreadcrumb(i)}>{seg.name}</Typography>
                : <Typography key={seg.id} variant="body2" fontWeight={600}>{seg.name}</Typography>,
            )}
            {folderPath.length === 0 && currentFolderId && (
              <Typography variant="body2" fontWeight={600}>Folder #{currentFolderId}</Typography>
            )}
          </Breadcrumbs>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="files-sort-label">Sort by</InputLabel>
              <Select
                labelId="files-sort-label"
                label="Sort by"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value)}
                renderValue={(v) => SORT_LABELS[v] || v}
              >
                <MenuItem value="newest">Newest first</MenuItem>
                <MenuItem value="oldest">Oldest first</MenuItem>
                <MenuItem value="name_asc">Name A–Z</MenuItem>
                <MenuItem value="name_desc">Name Z–A</MenuItem>
                <MenuItem value="size">Largest first</MenuItem>
              </Select>
            </FormControl>
            {uploadMut.isPending && <CircularProgress size={18} aria-label="Uploading" />}
            <Button variant="outlined" startIcon={<AddIcon />} sx={{ textTransform: 'none' }} onClick={() => setNewFolderOpen(true)}>New folder</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
          {[
            { k: 'all', label: 'All' },
            { k: 'documents', label: 'Documents' },
            { k: 'spreadsheets', label: 'Spreadsheets' },
            { k: 'images', label: 'Images' },
            { k: 'signed', label: 'Signed' },
            { k: 'pending', label: 'Pending signatures' },
          ].map(({ k, label }) => (
            <Chip
              key={k}
              label={label}
              size="small"
              onClick={() => setFilterChip(k)}
              color={filterChip === k ? 'primary' : 'default'}
              variant={filterChip === k ? 'filled' : 'outlined'}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by file name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ flex: '1 1 260px', maxWidth: 400 }}
          />
          {anySelected && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">{selected.length} selected</Typography>
              <Button size="small" variant="outlined" color="error" sx={{ textTransform: 'none' }}
                onClick={() => {
                  Promise.all(selected.map((id) => api.patch(`/api/multi-tenant/clients/${clientId}/files/${id}/`, { is_archived: true })))
                    .then(() => {
                      setSelected([]);
                      contentsQuery.refetch();
                      foldersQuery.refetch();
                      inboxCountQuery.refetch();
                      showToast('Files archived', 'success');
                    })
                    .catch(() => showToast('Archive failed', 'error'));
                }}
              >
                Archive selected
              </Button>
            </Box>
          )}
        </Box>

        {loading ? (
          <Box>{[...Array(5)].map((_, i) => <Skeleton key={i} variant="rounded" height={48} animation="wave" sx={{ mb: 0.75, borderRadius: 1 }} />)}</Box>
        ) : loadError ? (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'error.light', borderColor: 'error.main' }}>
            <Typography color="error" sx={{ fontWeight: 600, mb: 1 }}>Could not load files</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>{errorDetail || 'Unknown error'}</Typography>
            <Button variant="contained" color="error" onClick={() => { foldersQuery.refetch(); contentsQuery.refetch(); }} sx={{ textTransform: 'none' }}>Retry</Button>
          </Paper>
        ) : (
          <>
            {currentFolderId && subfolders.length > 0 && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, mb: 0.5, display: 'block' }}>Folders</Typography>
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableBody>
                      {subfolders.map((f) => (
                        <TableRow
                          key={f.id}
                          hover
                          onClick={() => handleFolderClick(f)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, '& td': { borderBottom: 1, borderColor: 'divider' } }}
                        >
                          <TableCell sx={{ width: 40 }}><FolderOutlinedIcon fontSize="small" color="primary" /></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={500}>{f.name}</Typography></TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', width: 140 }}><Typography variant="caption">{fmtDate(f.updated_at || f.created_at)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {!currentFolderId && folders.length > 0 && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, mb: 0.5, display: 'block' }}>Folders</Typography>
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableBody>
                      {folders.map((f) => (
                        <TableRow
                          key={f.id}
                          hover
                          onClick={() => handleFolderClick(f)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, '& td': { borderBottom: 1, borderColor: 'divider' } }}
                        >
                          <TableCell sx={{ width: 40 }}><FolderOutlinedIcon fontSize="small" color="primary" /></TableCell>
                          <TableCell><Typography variant="body2" fontWeight={500}>{f.name}</Typography></TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', width: 140 }}><Typography variant="caption">{fmtDate(f.updated_at || f.created_at)}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, mb: 0.5, display: 'block' }}>Files</Typography>
            {filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2, display: 'block', mx: 'auto' }} />
                <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
                  {search || filterChip !== 'all'
                    ? 'No files match'
                    : filesView === 'inbox'
                      ? 'Inbox is empty'
                      : filesView === 'archived'
                        ? 'No archived files'
                        : 'No files yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {search || filterChip !== 'all'
                    ? 'Try adjusting search or filters.'
                    : filesView === 'inbox'
                      ? 'When clients upload requested documents, they appear here. Use Upload files to add documents yourself.'
                      : filesView === 'archived'
                        ? 'Archived files are hidden from the main list.'
                        : 'Upload your first document to get started.'}
                </Typography>
                {search || filterChip !== 'all' ? (
                  <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={() => { setSearch(''); setFilterChip('all'); }}>Clear filters</Button>
                ) : (
                  <Button variant="contained" sx={{ textTransform: 'none' }} onClick={() => fileInputRef.current?.click()}>Upload files</Button>
                )}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" sx={{ '& .row-actions': { opacity: { xs: 1, md: 0 } }, '& tbody tr:hover .row-actions': { opacity: 1 } }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { color: 'text.secondary', fontWeight: 600, borderBottom: 1, borderColor: 'divider' } }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={allSelected} indeterminate={anySelected && !allSelected} onChange={toggleAll} inputProps={{ 'aria-label': 'Select all files' }} />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>Size</TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>Date</TableCell>
                      <TableCell sx={{ width: 140 }}>Uploaded by</TableCell>
                      <TableCell align="right" sx={{ width: 56 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((f) => {
                      const fid = String(f.id);
                      const isHighlighted = highlightFileId && (highlightFileId === fid || highlightFileId === `file-${f.id}` || highlightFileId === `file-dr_${fid.replace('dr_', '')}`);
                      return (
                        <TableRow
                          key={fid}
                          id={`file-${f.id}`}
                          hover
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData('application/x-firmflow-file-id', String(f.id));
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => setFolderDropTarget(null)}
                          selected={selected.includes(fid)}
                          tabIndex={0}
                          onClick={() => handleRowClick(f)}
                          onDoubleClick={() => handleRowDoubleClick(f)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setMenuFile(f);
                            setContextMenu({ mouseX: e.clientX - 2, mouseY: e.clientY - 4 });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openPreview(f, false);
                            }
                          }}
                          sx={{
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' },
                            '&:hover': { bgcolor: 'action.hover' },
                            '& td': { borderBottom: 1, borderColor: 'divider' },
                            ...(isHighlighted ? { bgcolor: 'action.selected' } : {}),
                          }}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox size="small" checked={selected.includes(fid)} onChange={() => toggleSelect(fid)} inputProps={{ 'aria-label': `Select ${f.file_name}` }} />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              {kindIcon(f.file_name)}
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>{f.file_name}</Typography>
                              {isDocReq(f) && <Chip label="Client upload" size="small" sx={{ height: 18, fontSize: 11, flexShrink: 0 }} />}
                              {f.shared_with_client && <Chip label="Shared" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: 11, flexShrink: 0 }} />}
                              {f.ai_classification && !isDocReq(f) && typeof f.ai_classification === 'string' && (
                                <Chip label={f.ai_classification} size="small" sx={{ height: 18, fontSize: 11, flexShrink: 0 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{fmtSize(f.file_size)}</TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{fmtDate(f.uploaded_at)}</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{f.uploaded_by}</TableCell>
                          <TableCell align="right" className="row-actions" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="More actions">
                              <IconButton
                                size="small"
                                aria-label={`More actions for ${f.file_name}`}
                                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); setMenuFile(f); }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && Boolean(menuFile)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ autoFocusItem: true }}
      >
        <MenuItem onClick={() => runMenuAction((f) => openPreview(f, false))}><VisibilityIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />Preview</MenuItem>
        <MenuItem onClick={() => runMenuAction((f) => handleDownload(f))}><DownloadIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />Download</MenuItem>
        {!isDocReq(menuFile) && <MenuItem onClick={() => runMenuAction((f) => setRenameDialog({ open: true, file: f, value: f?.file_name || '' }))}><EditIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />Rename</MenuItem>}
        <MenuItem onClick={() => runMenuAction((f) => { setMoveTarget(f); setMoveOpen(true); })}><DriveFileMoveIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />Move to folder</MenuItem>
        <MenuItem onClick={() => runMenuAction((f) => handleShare(f))}>
          <ShareIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
          Share with client
          {menuFile?.shared_with_client ? <Chip label="Shared" size="small" sx={{ ml: 1, height: 18 }} /> : null}
        </MenuItem>
        {!isDocReq(menuFile) && (
          <MenuItem onClick={() => runMenuAction((f) => handleSign(f))}>
            <BorderColorIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />
            E-signature — place fields on PDF
          </MenuItem>
        )}
        {!isDocReq(menuFile) && <MenuItem onClick={() => runMenuAction((f) => handleVersionHistory(f))}><HistoryIcon fontSize="small" sx={{ mr: 1.5, color: 'text.secondary' }} />Version history</MenuItem>}
        <Divider />
        <MenuItem onClick={() => runMenuAction((f) => setDeleteDialog({ open: true, file: f }))} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />Delete</MenuItem>
      </Menu>

      <Menu
        open={Boolean(contextMenu)}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <MenuList autoFocusItem dense component="div">
          <MenuItem onClick={() => runMenuAction((f) => openPreview(f, false))}><VisibilityIcon fontSize="small" sx={{ mr: 1.5 }} />Preview</MenuItem>
          <MenuItem onClick={() => runMenuAction((f) => handleDownload(f))}><DownloadIcon fontSize="small" sx={{ mr: 1.5 }} />Download</MenuItem>
          {!isDocReq(menuFile) && <MenuItem onClick={() => runMenuAction((f) => setRenameDialog({ open: true, file: f, value: f?.file_name || '' }))}><EditIcon fontSize="small" sx={{ mr: 1.5 }} />Rename</MenuItem>}
          <MenuItem onClick={() => runMenuAction((f) => { setMoveTarget(f); setMoveOpen(true); })}><DriveFileMoveIcon fontSize="small" sx={{ mr: 1.5 }} />Move to folder</MenuItem>
          <MenuItem onClick={() => runMenuAction((f) => handleShare(f))}><ShareIcon fontSize="small" sx={{ mr: 1.5 }} />Share with client</MenuItem>
          {!isDocReq(menuFile) && (
            <MenuItem onClick={() => runMenuAction((f) => handleSign(f))}>
              <BorderColorIcon fontSize="small" sx={{ mr: 1.5 }} />
              E-signature — place fields on PDF
            </MenuItem>
          )}
          {!isDocReq(menuFile) && <MenuItem onClick={() => runMenuAction((f) => handleVersionHistory(f))}><HistoryIcon fontSize="small" sx={{ mr: 1.5 }} />Version history</MenuItem>}
          <Divider />
          <MenuItem onClick={() => runMenuAction((f) => setDeleteDialog({ open: true, file: f }))} sx={{ color: 'error.main' }}><DeleteIcon fontSize="small" sx={{ mr: 1.5 }} />Delete</MenuItem>
        </MenuList>
      </Menu>

      <PreviewDrawer
        open={Boolean(previewFile) && !previewFullscreen}
        fullScreen={false}
        onClose={() => { setPreviewFile(null); setPreviewFullscreen(false); }}
        file={previewFile}
        folderPathLabel={folderPathLabel}
        sharedWithClient={previewFile?.shared_with_client}
        onDownload={handleDownload}
        onShare={handleShare}
        onDelete={(file) => { setPreviewFile(null); setDeleteDialog({ open: true, file }); }}
        onOpenVersionHistory={(file) => handleVersionHistory(file)}
        onPlaceSignatureFields={
          previewFile && !isDocReq(previewFile) ? () => handleSign(previewFile) : undefined
        }
      />
      <PreviewDrawer
        open={Boolean(previewFile) && previewFullscreen}
        fullScreen
        onClose={() => { setPreviewFile(null); setPreviewFullscreen(false); }}
        file={previewFile}
        folderPathLabel={folderPathLabel}
        sharedWithClient={previewFile?.shared_with_client}
        onDownload={handleDownload}
        onShare={handleShare}
        onDelete={(file) => { setPreviewFile(null); setDeleteDialog({ open: true, file }); }}
        onOpenVersionHistory={(file) => handleVersionHistory(file)}
        onPlaceSignatureFields={
          previewFile && !isDocReq(previewFile) ? () => handleSign(previewFile) : undefined
        }
      />

      <MoveFolderDialog
        open={moveOpen}
        onClose={() => { setMoveOpen(false); setMoveTarget(null); }}
        fileName={moveTarget?.file_name}
        topLevelFolders={folders}
        subfolders={subfolders}
        onSelect={(folderId) => {
          if (!moveTarget) return;
          moveMut.mutate(
            { fileId: moveTarget.id, folderId },
            { onSettled: refetchAfterMove },
          );
          setMoveOpen(false);
          setMoveTarget(null);
        }}
      />

      <Dialog open={renameDialog.open} onClose={() => setRenameDialog({ open: false, file: null, value: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Rename file</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="File name"
            value={renameDialog.value}
            sx={{ mt: 1 }}
            helperText="Max 255 characters. No slashes."
            onChange={(e) => setRenameDialog((d) => ({ ...d, value: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSubmit(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ open: false, file: null, value: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleRenameSubmit} disabled={renameMut.isPending}>Rename</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, file: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete file</DialogTitle>
        <DialogContent>
          <Typography>
            Delete &quot;{deleteDialog.file?.file_name}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, file: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMut.isPending}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={versionDialog.open} onClose={() => setVersionDialog({ open: false, file: null, versions: [], loading: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Version history — {versionDialog.file?.file_name}</DialogTitle>
        <DialogContent>
          {versionDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : versionDialog.versions.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>No previous versions found.</Typography>
          ) : (
            <List dense>
              {versionDialog.versions.map((v, i) => (
                <ListItemButton key={v.id || i}>
                  <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary={`Version ${versionDialog.versions.length - i}`} secondary={fmtDate(v.created_at || v.version_date)} />
                  {(v.file_url || v.url) && <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => window.open(v.file_url || v.url, '_blank', 'noopener,noreferrer')}>Open</Button>}
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setVersionDialog({ open: false, file: null, versions: [], loading: false })}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={newFolderOpen} onClose={() => { setNewFolderOpen(false); setNewFolderName(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Folder name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNewFolderOpen(false); setNewFolderName(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFolder} disabled={createFolderMut.isPending}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(esignPrepare)}
        onClose={() => setEsignPrepare(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'background.default' } }}
      >
        <DialogContent sx={{ p: { xs: 1, sm: 2 }, height: '100%', boxSizing: 'border-box' }}>
          {esignPrepare && (
            <ESignaturePrepare
              documentId={esignPrepare.documentId}
              documentName={esignPrepare.fileName}
              pdfUrl={esignPrepare.pdfUrl}
              onBack={() => setEsignPrepare(null)}
              onSent={() => {
                setEsignPrepare(null);
                queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'clientFileContents' && q.queryKey[1] === clientId });
                queryClient.invalidateQueries({ queryKey: clientFilesKeys.folders(clientId) });
                showToast('Document sent to client for signing', 'success');
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ width: '100%' }}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FilesTab;
